import {
    DROPDOWN_STRING,
    MULTILINK,
    OPTIONAL_STRING,
    OPTIONAL_TEXT,
    OPTIONAL_TEXT_3000,
    REQUIRED_STRING,
    YEAR,
} from '@/components/Form/schema';
import { FormErrors } from '@/components/Form/types';
import { uniqueNameShortField } from '@/forms/forms';
import { GetProjectByNameDocument, GetProjectSeriesByNameDocument } from '@/generated/graphql';
import { z } from 'zod';

const PROJECT_SERIES_NAME_SHORT_FIELD = uniqueNameShortField(GetProjectSeriesByNameDocument);
const PROJECT_NAME_SHORT_FIELD = uniqueNameShortField(GetProjectByNameDocument);

export const ProjectSeriesCreateSchema = z.object({
    singleProject: z.boolean().default(false),
    nameShort: PROJECT_SERIES_NAME_SHORT_FIELD,
    nameLong: z.string().min(1),
});

export type ProjectSeriesCreateSchemaType = z.infer<typeof ProjectSeriesCreateSchema>;
export type ProjectSeriesCreateErrors = FormErrors<ProjectSeriesCreateSchemaType>;

export const ProjectCreateSchema = z.object({
    nameShort: PROJECT_NAME_SHORT_FIELD,
    nameLong: REQUIRED_STRING,
    executionYear: YEAR,
    status: DROPDOWN_STRING,
});
export type ProjectCreateSchemaType = z.infer<typeof ProjectCreateSchema>;
export type ProjectCreateErrors = FormErrors<ProjectCreateSchemaType>;

export const ProjectSeriesUpdateSchema = z.object({
    nameShort: PROJECT_SERIES_NAME_SHORT_FIELD,
    nameLong: REQUIRED_STRING,
    notes: OPTIONAL_STRING,
    description: OPTIONAL_STRING,
    websites: MULTILINK,
    category: OPTIONAL_STRING,
    type: OPTIONAL_STRING,
    companyGroup: OPTIONAL_STRING,
    businessDomain: OPTIONAL_STRING,
    department: OPTIONAL_STRING,
    organisationalChanges: OPTIONAL_STRING,
    cycle: OPTIONAL_STRING,
    notesCycle: OPTIONAL_TEXT,
    notesDayOrder: OPTIONAL_TEXT,
    notesAppointment: OPTIONAL_TEXT,
    locationRequirements: OPTIONAL_TEXT,
    allocationHints: OPTIONAL_TEXT,
    otherDocuments: MULTILINK,
    otherSystems: MULTILINK,
    otherLinks: MULTILINK,
});
export type ProjectSeriesUpdateSchemaType = z.infer<typeof ProjectSeriesUpdateSchema>;

export function getProjectSeriesIdentifiersSchema(id: string) {
    return z.object({
        nameShort: uniqueNameShortField(GetProjectSeriesByNameDocument, id),
        nameLong: REQUIRED_STRING,
        notes: OPTIONAL_TEXT_3000,
        description: OPTIONAL_TEXT_3000,
        websites: MULTILINK,
    });
}

export const ProjectSeriesTypeDefSchema = ProjectSeriesUpdateSchema.pick({
    type: true,
    category: true,
});
export type ProjectSeriesTypeDefSchemaType = z.infer<typeof ProjectSeriesTypeDefSchema>;

export const ProjectSeriesOrgSchema = ProjectSeriesUpdateSchema.pick({
    companyGroup: true,
    businessDomain: true,
    department: true,
    organisationalChanges: true,
});
export type ProjectSeriesOrgSchemaType = z.infer<typeof ProjectSeriesOrgSchema>;

export const ProjectSeriesCycleSchema = ProjectSeriesUpdateSchema.pick({
    cycle: true,
    notesCycle: true,
});
export type ProjectSeriesCycleSchemaType = z.infer<typeof ProjectSeriesCycleSchema>;

export const ProjectSeriesAppointmentSchema = ProjectSeriesUpdateSchema.pick({
    notesDayOrder: true,
    notesAppointment: true,
});
export type ProjectSeriesAppointmentSchemaType = z.infer<typeof ProjectSeriesAppointmentSchema>;

export const ProjectSeriesLocationSchema = ProjectSeriesUpdateSchema.pick({
    locationRequirements: true,
    allocationHints: true,
});
export type ProjectSeriesLocationSchemaType = z.infer<typeof ProjectSeriesLocationSchema>;

export const ProjectSeriesDocumentsSchema = ProjectSeriesUpdateSchema.pick({
    otherDocuments: true,
});
export type ProjectSeriesDocumentsSchemaType = z.infer<typeof ProjectSeriesDocumentsSchema>;

export const ProjectSeriesSystemsSchema = ProjectSeriesUpdateSchema.pick({
    otherSystems: true,
});
export type ProjectSeriesSystemsSchemaType = z.infer<typeof ProjectSeriesSystemsSchema>;

export const ProjectSeriesOtherLinksSchema = ProjectSeriesUpdateSchema.pick({
    otherLinks: true,
});
export type ProjectSeriesOtherLinksSchemaType = z.infer<typeof ProjectSeriesOtherLinksSchema>;

// PROJECT SCHEMAS

export const ProjectUpdateSchema = z.object({
    nameShort: PROJECT_NAME_SHORT_FIELD,
    nameLong: REQUIRED_STRING,
    subtitle: OPTIONAL_STRING,
    executionYear: YEAR,
    description: OPTIONAL_TEXT_3000,
    category: OPTIONAL_STRING,
    type: OPTIONAL_STRING,
    companyGroup: OPTIONAL_STRING,
    businessDomain: OPTIONAL_STRING,
    department: OPTIONAL_STRING,
    organisationalChanges: OPTIONAL_STRING,
    disturbanceNoise: OPTIONAL_STRING,
    disturbanceDust: OPTIONAL_STRING,
    disturbanceOther: OPTIONAL_STRING,
    notesDayOrder: OPTIONAL_STRING,
    notesAppointment: OPTIONAL_STRING,
    locationRequirements: OPTIONAL_STRING,
    allocationHints: OPTIONAL_STRING,
    otherDocuments: MULTILINK,
    otherSystems: MULTILINK,
    otherLinks: MULTILINK,
});
export type ProjectUpdateSchemaType = z.infer<typeof ProjectUpdateSchema>;

export function getProjectIdentifiersSchema(id: string) {
    return z.object({
        nameShort: uniqueNameShortField(GetProjectByNameDocument, id),
        nameLong: REQUIRED_STRING,
        subtitle: OPTIONAL_STRING,
        executionYear: YEAR,
        description: OPTIONAL_TEXT_3000,
    });
}

export const ProjectTypeDefSchema = ProjectUpdateSchema.pick({
    type: true,
    category: true,
});
export type ProjectTypeDefSchemaType = z.infer<typeof ProjectTypeDefSchema>;

export const ProjectDisturbanceSchema = ProjectUpdateSchema.pick({
    disturbanceNoise: true,
    disturbanceDust: true,
    disturbanceOther: true,
});
export type ProjectDisturbanceSchemaType = z.infer<typeof ProjectDisturbanceSchema>;

export const ProjectOrgSchema = ProjectUpdateSchema.pick({
    companyGroup: true,
    businessDomain: true,
    department: true,
    organisationalChanges: true,
});
export type ProjectOrgSchemaType = z.infer<typeof ProjectOrgSchema>;

export const ProjectAppointmentSchema = ProjectUpdateSchema.pick({
    notesDayOrder: true,
    notesAppointment: true,
});
export type ProjectAppointmentSchemaType = z.infer<typeof ProjectAppointmentSchema>;

export const ProjectLocationSchema = ProjectUpdateSchema.pick({
    locationRequirements: true,
    allocationHints: true,
});
export type ProjectLocationSchemaType = z.infer<typeof ProjectLocationSchema>;

export const ProjectDocumentsSchema = ProjectUpdateSchema.pick({
    otherDocuments: true,
});
export type ProjectDocumentsSchemaType = z.infer<typeof ProjectDocumentsSchema>;

export const ProjectSystemsSchema = ProjectUpdateSchema.pick({
    otherSystems: true,
});
export type ProjectSystemsSchemaType = z.infer<typeof ProjectSystemsSchema>;

export const ProjectOtherLinksSchema = ProjectUpdateSchema.pick({
    otherLinks: true,
});
export type ProjectOtherLinksSchemaType = z.infer<typeof ProjectOtherLinksSchema>;
